import { useSceneEvents } from "@/components/common/scene-events-context";
import { dilate } from "@/tools/floor-area/grid-mask";
import { AdaptivePointsMaterial, LodPointCloud } from "@faro-lotv/lotv";
import { useEffect, useMemo } from "react";
import {
  Color,
  DataArrayTexture,
  FloatType,
  IUniform,
  LinearSRGBColorSpace,
  RGFormat,
  Vector2,
  Vector3,
} from "three";
import { FloorAreaEstimate } from "./floor-area-store";

/** Distinct colors to tell the floors apart, by floor index */
export const FLOOR_COLORS = [
  "#2f80ed",
  "#27ae60",
  "#f2994a",
  "#9b51e0",
  "#eb5757",
  "#00a3a3",
];

const MAX_FLOORS = 16;
/** Tints every point of a storey, from its floor up to the next floor, walls and ceilings included, else only the floors */
const TINT_WHOLE_STOREYS = true;
/** Points this close to the surface of a floor are on the floor */
const FLOOR_BAND = 0.15;
/** Share of the floor color on the floor surfaces, the rest keeps the color of the point */
const FLOOR_TINT = 0.6;
/** Share of the floor color on the rest of the storey, e.g. walls, so the floor stands out */
const WALL_TINT = 0.25;
/** The walls of a storey are this close to its floor, farther points above it are outside, e.g. trees */
const WALL_REACH = 1;
/** Height of the cells outside a floor, so no point matches them */
const NO_FLOOR = -1e30;
/**
 * Share of the points of the root node of the other storeys still drawn while one storey is isolated,
 * a quarter of it at each deeper level, so they stay as sparse when more details load
 */
const GHOST_ROOT_SHARE = 0.5;
/** How much the points of the other storeys fade to grey while one storey is isolated */
const GHOST_FADE = 0.7;
/** How much the faded points of the other storeys then fade to white, so they recede like a background */
const GHOST_WHITEN = 0.7;
/** Size of the points of the other storeys, in CSS pixels, also while navigating when the points grow */
const GHOST_POINT_SIZE = 2;

const UNIFORMS_GLSL = `
#define FLOOR_STOREYS ${Number(TINT_WHOLE_STOREYS)}
uniform highp sampler2DArray uFloorHeights;
uniform vec3 uFloorOrigin;
uniform vec2 uFloorGridSize;
uniform float uFloorCellSize;
uniform int uFloorCount;
uniform float uFloorElevations[${MAX_FLOORS}];
uniform vec3 uFloorColors[${MAX_FLOORS}];
uniform bool uFloorTint;
uniform int uFloorIsolated;
`;

const TINT_GLSL = `
#if defined(USE_COLOR) || defined(USE_COLOR_ALPHA)
{
  vec3 floorPosition = (modelMatrix * vec4(transformed, 1.0)).xyz - uFloorOrigin;
  ivec2 cell = ivec2(floor(floorPosition.xz / uFloorCellSize));
  bool isInGrid = all(greaterThanEqual(cell, ivec2(0))) && all(lessThan(cell, ivec2(uFloorGridSize)));
  // The lowest storey has everything not on an upper storey
  int storey = 0;
  for (int i = 1; i < ${MAX_FLOORS}; i++) {
    if (i >= uFloorCount || floorPosition.y < uFloorElevations[i] - ${FLOOR_BAND.toFixed(2)}) break;
    if (isInGrid && texelFetch(uFloorHeights, ivec3(cell, i), 0).g > 0.5) storey = i;
  }
  if (uFloorTint) {
#if FLOOR_STOREYS
    float floorHeight = isInGrid ? texelFetch(uFloorHeights, ivec3(cell, storey), 0).r : ${NO_FLOOR.toExponential()};
    bool isOnFloor = abs(floorPosition.y - floorHeight) < ${FLOOR_BAND.toFixed(2)};
    vColor.rgb = mix(vColor.rgb, uFloorColors[storey], isOnFloor ? ${FLOOR_TINT.toFixed(2)} : ${WALL_TINT.toFixed(2)});
#else
    if (isInGrid) {
      for (int i = 0; i < ${MAX_FLOORS}; i++) {
        if (i >= uFloorCount) break;
        if (abs(floorPosition.y - texelFetch(uFloorHeights, ivec3(cell, i), 0).r) < ${FLOOR_BAND.toFixed(2)}) {
          vColor.rgb = mix(vColor.rgb, uFloorColors[i], ${FLOOR_TINT.toFixed(2)});
          break;
        }
      }
    }
#endif
  }
  if (uFloorIsolated >= 0 && storey != uFloorIsolated) {
    // Same points kept every frame, so the other storeys look see-through without flickering
    uint hash = uint(gl_VertexID) * 2654435761u;
    hash ^= hash >> 16;
    // Each level has about 4 times the surface density of its parent
    if (float(hash) / 4294967295.0 > ${GHOST_ROOT_SHARE.toFixed(2)} * pow(0.25, uLevel)) gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    // The adaptive size would grow the kept points back into a solid surface
    gl_PointSize = uDpr * ${GHOST_POINT_SIZE.toFixed(1)};
    float luma = dot(vColor.rgb, vec3(0.2126, 0.7152, 0.0722));
    vColor.rgb = mix(mix(vColor.rgb, vec3(luma), ${GHOST_FADE.toFixed(2)}), vec3(1.0), ${GHOST_WHITEN.toFixed(2)});
  }
}
#endif
`;

type FloorColoringOptions = {
  /** Tint the points with the color of their floor */
  isTinted: boolean;
  /** Index of the level to show alone, the others fade out */
  isolatedFloor?: number;
};

/**
 * Tints the points of the floors of the estimate with the color of their floor, and isolates a floor, in the shader of the point cloud
 *
 * @param pointCloud The point cloud to color
 * @param estimate The floors to color, none if undefined
 * @param options How to show the floors
 */
export function useFloorColoring(
  pointCloud: LodPointCloud,
  estimate: FloorAreaEstimate | undefined,
  { isTinted, isolatedFloor }: FloorColoringOptions,
): void {
  const { invalidatePointCloudScene } = useSceneEvents();
  // Updated on their own, so hovering a floor does not recompile the shader
  const displayUniforms = useMemo(
    () => ({
      uFloorTint: { value: false },
      uFloorIsolated: { value: -1 },
    }),
    [],
  );

  useEffect(() => {
    displayUniforms.uFloorTint.value = isTinted;
    displayUniforms.uFloorIsolated.value =
      isolatedFloor !== undefined && isolatedFloor < MAX_FLOORS
        ? isolatedFloor
        : -1;
    if (pointCloud.material instanceof AdaptivePointsMaterial) {
      pointCloud.material.uniformsNeedUpdate = true;
    }
    invalidatePointCloudScene.emit();
  }, [
    displayUniforms,
    invalidatePointCloudScene,
    isTinted,
    isolatedFloor,
    pointCloud,
  ]);

  useEffect(() => {
    const { material } = pointCloud;
    if (
      !estimate?.levels.length ||
      !(material instanceof AdaptivePointsMaterial)
    ) {
      return;
    }
    const levels = estimate.levels.slice(0, MAX_FLOORS);
    const { width, height, cellSize, minX, minZ } = levels[0].footprint;
    const reach = Math.ceil(WALL_REACH / cellSize);
    // Per cell and level: the floor height, and whether the cell is on the floor or its walls
    const cells = new Float32Array(width * height * levels.length * 2);
    levels.forEach((level, i) => {
      const nearFloor = dilate(level.footprint, reach).data;
      const offset = i * width * height;
      level.heights.forEach((value, cell) => {
        cells[(offset + cell) * 2] = Number.isNaN(value) ? NO_FLOOR : value;
        cells[(offset + cell) * 2 + 1] = nearFloor[cell];
      });
    });
    const texture = new DataArrayTexture(cells, width, height, levels.length);
    texture.format = RGFormat;
    texture.type = FloatType;
    texture.needsUpdate = true;

    const uniforms: Record<string, IUniform> = {
      uFloorHeights: { value: texture },
      uFloorOrigin: {
        value: estimate.origin.clone().add(new Vector3(minX, 0, minZ)),
      },
      uFloorGridSize: { value: new Vector2(width, height) },
      uFloorCellSize: { value: cellSize },
      uFloorCount: { value: levels.length },
      // Heights relative to the origin, the upper levels from the lowest
      uFloorElevations: {
        value: Array.from({ length: MAX_FLOORS }, (_, i) =>
          i < levels.length ? levels[i].elevation : 0,
        ),
      },
      uFloorColors: {
        value: Array.from({ length: MAX_FLOORS }, (_, i) =>
          // The point colors are not converted, so neither are the floor colors
          new Color().setStyle(
            FLOOR_COLORS[i % FLOOR_COLORS.length],
            LinearSRGBColorSpace,
          ),
        ),
      },
      ...displayUniforms,
    };
    Object.assign(material.uniforms, uniforms);
    const compile = material.onBeforeCompile.bind(material);
    material.onBeforeCompile = (shader, renderer) => {
      compile(shader, renderer);
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", `#include <common>\n${UNIFORMS_GLSL}`)
        .replace(
          "#include <fog_vertex>",
          `#include <fog_vertex>\n${TINT_GLSL}`,
        );
    };
    material.needsUpdate = true;

    return () => {
      // Back to the onBeforeCompile of the class
      Reflect.deleteProperty(material, "onBeforeCompile");
      for (const name of Object.keys(uniforms)) {
        Reflect.deleteProperty(material.uniforms, name);
      }
      material.needsUpdate = true;
      texture.dispose();
    };
  }, [displayUniforms, estimate, pointCloud]);
}
